import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useSupportCasesStore } from 'dashboard/stores/supportCases';
import { PRIORITY_CONDITION_VALUES } from 'dashboard/constants/automation';

/**
 * Composable for handling macro-related functionality
 * @returns {Object} An object containing the getMacroDropdownValues function
 */
export const useMacros = () => {
  const { t } = useI18n();
  const getters = useStoreGetters();

  const labels = computed(() => getters['labels/getLabels'].value);
  const teams = computed(() => getters['teams/getTeams'].value);
  const agents = computed(() => getters['agents/getVerifiedAgents'].value);
  const casesStore = useSupportCasesStore();

  // A stage belongs to one ticket type, so the option has to name both: the macro
  // moves only the linked tickets of that type, and two workflows may well both
  // have a stage called "In progress".
  const caseStages = computed(() =>
    casesStore.caseTypeOptions.flatMap(caseType =>
      (caseType.workflowStages || []).map(stage => ({
        id: stage.id,
        name: `${caseType.name} › ${stage.name}`,
      }))
    )
  );

  const withNoneOption = options => [
    { id: 'nil', name: t('AUTOMATION.NONE_OPTION') },
    ...(options || []),
  ];

  /**
   * Get dropdown values based on the specified type
   * @param {string} type - The type of dropdown values to retrieve
   * @returns {Array} An array of dropdown values
   */
  const getMacroDropdownValues = type => {
    switch (type) {
      case 'assign_team':
        return withNoneOption(teams.value);
      case 'send_email_to_team':
        return teams.value;
      case 'assign_agent':
        return [
          ...withNoneOption(),
          { id: 'self', name: 'Self' },
          ...agents.value,
        ];
      // Ticket actions state the whole owner, so neither offers a "None" option:
      // clearing a ticket owner is not something a macro does.
      case 'assign_case_team':
        return teams.value;
      case 'assign_case_agent':
        return [{ id: 'self', name: 'Self' }, ...agents.value];
      case 'change_case_stage':
        return caseStages.value;
      case 'add_label':
      case 'remove_label':
        return labels.value.map(i => ({
          id: i.title,
          name: i.title,
        }));
      case 'change_priority':
        return PRIORITY_CONDITION_VALUES.map(item => ({
          id: item.id,
          name: t(`MACROS.PRIORITY_TYPES.${item.i18nKey}`),
        }));
      default:
        return [];
    }
  };

  return {
    getMacroDropdownValues,
  };
};
